import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SEVERITY_COLORS = {
  high: '#ef4444',    // red-500
  medium: '#f59e0b',  // amber-500
  low: '#3b82f6',     // blue-500
};

const NO_DATA_COLOR = '#3f3f46'; // zinc-700

const MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_NAMES_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const RuleBreakingCalendar = ({ data = [] }) => {
  const [view, setView] = useState('monthly'); // 'monthly' | 'yearly'
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return { year: now.getUTCFullYear(), month: now.getUTCMonth() };
  });

  // Build data lookup map (date -> rule breaks)
  const dataMap = useMemo(() => {
    const map = new Map();
    data.forEach(entry => {
      const dateStr = entry.date.split('T')[0];
      map.set(dateStr, entry);
    });
    return map;
  }, [data]);

  // Monthly grid computation (similar to EmotionalHeatmap)
  const monthGrid = useMemo(() => {
    if (view !== 'monthly') return null;

    const { year, month } = selectedMonth;

    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const daysInMonth = lastDay.getUTCDate();
    const startDow = firstDay.getUTCDay(); // 0=Sun

    const weeks = [];
    let currentWeek = new Array(startDow).fill(null);

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const entry = dataMap.get(dateStr);
      currentWeek.push(entry || { date: dateStr, _empty: true });

      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }

    return { weeks, title: `${MONTH_NAMES_FULL[month]} ${year}` };
  }, [view, selectedMonth, dataMap]);

  // Yearly view: weeks and month labels (similar to EmotionalHeatmap)
  const { weeks, monthLabels } = useMemo(() => {
    if (view !== 'yearly' || !data.length) return { weeks: [], monthLabels: [] };

    // Find the date range
    const dates = data.map(d => new Date(d.date));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));

    // Find the Sunday before minDate (UTC)
    const startDate = new Date(minDate);
    startDate.setUTCDate(startDate.getUTCDate() - startDate.getUTCDay());

    // Find the Saturday after maxDate (UTC)
    const endDate = new Date(maxDate);
    endDate.setUTCDate(endDate.getUTCDate() + (6 - endDate.getUTCDay()));

    // Generate all weeks
    const weeks = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const week = [];

      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        const dayDate = new Date(currentDate);
        dayDate.setUTCDate(dayDate.getUTCDate() + dayIdx);

        const dateStr = dayDate.toISOString().split('T')[0];
        const entry = dataMap.get(dateStr);
        week.push(entry || null);
      }

      weeks.push(week);
      currentDate.setUTCDate(currentDate.getUTCDate() + 7);
    }

    // Generate month labels using calculated dates
    const labels = [];
    let lastMonth = -1;

    for (let weekIndex = 0; weekIndex < weeks.length; weekIndex++) {
      const weekStartDate = new Date(startDate);
      weekStartDate.setUTCDate(weekStartDate.getUTCDate() + weekIndex * 7);

      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        const dayDate = new Date(weekStartDate);
        dayDate.setUTCDate(dayDate.getUTCDate() + dayIdx);
        const month = dayDate.getUTCMonth();

        if (month !== lastMonth) {
          labels.push({
            weekIndex,
            label: MONTH_NAMES_SHORT[month],
          });
          lastMonth = month;
          break;
        }
      }
    }

    return { weeks, monthLabels: labels };
  }, [data, dataMap, view]);

  const goToPrevMonth = () => {
    setSelectedMonth(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { ...prev, month: prev.month - 1 };
    });
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { ...prev, month: prev.month + 1 };
    });
  };

  const getCellColor = (entry) => {
    if (!entry || entry._empty) return NO_DATA_COLOR;
    if (!entry.rule_breaks || entry.rule_breaks.length === 0) return NO_DATA_COLOR;
    return SEVERITY_COLORS[entry.max_severity] || SEVERITY_COLORS.medium;
  };

  const getCellOpacity = (entry) => {
    if (!entry || entry._empty) return 0.4;
    if (!entry.rule_breaks || entry.rule_breaks.length === 0) return 0.4;
    // Opacity based on count: 1-2 breaks = 0.6, 3-4 = 0.8, 5+ = 1.0
    const count = entry.total_count || 0;
    if (count >= 5) return 1.0;
    if (count >= 3) return 0.8;
    return 0.6;
  };

  const getTooltipText = (entry) => {
    if (!entry || entry._empty || !entry.rule_breaks || entry.rule_breaks.length === 0) {
      return `${entry?.date || ''} - No rule breaks`;
    }

    const ruleList = entry.rule_breaks
      .map(rb => `• ${rb.tag_label} (${rb.severity})`)
      .join('\n');

    return `${entry.date}\n${entry.total_count} rule break${entry.total_count > 1 ? 's' : ''}:\n${ruleList}`;
  };

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-foreground">Rule Breaking Calendar</h3>
        <div className="flex items-center gap-3">
          {/* View toggle: Monthly / Yearly */}
          <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
            <button
              onClick={() => setView('monthly')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                view === 'monthly'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setView('yearly')}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                view === 'yearly'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
          No rule breaks detected. Keep up the discipline!
        </div>
      ) : (
        <>
          {/* Monthly View */}
          {view === 'monthly' && monthGrid && (
            <div>
              {/* Month navigation */}
              <div className="flex items-center justify-center gap-4 mb-3">
                <button
                  onClick={goToPrevMonth}
                  className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium text-foreground min-w-[140px] text-center">
                  {monthGrid.title}
                </span>
                <button
                  onClick={goToNextMonth}
                  className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Day-of-week headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {dayLabels.map(d => (
                  <div key={d} className="text-[10px] text-muted-foreground text-center">
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="space-y-1">
                {monthGrid.weeks.map((week, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-1">
                    {week.map((entry, di) => {
                      if (entry === null) {
                        return <div key={di} className="w-full aspect-square" />;
                      }
                      const dayNum = parseInt(entry.date.split('-')[2], 10);
                      const hasBreaks = !entry._empty && entry.rule_breaks && entry.rule_breaks.length > 0;
                      return (
                        <div
                          key={di}
                          className="w-full aspect-square overflow-hidden rounded-md flex flex-col items-center p-1.5 cursor-pointer transition-transform hover:scale-[1.03]"
                          style={{
                            backgroundColor: getCellColor(entry),
                            opacity: getCellOpacity(entry),
                          }}
                        >
                          <span className="text-base font-bold text-white text-center leading-tight">{dayNum}</span>
                          {hasBreaks ? (
                            <div className="mt-0.5 w-full overflow-hidden text-[10px] text-white/70 leading-[1.3] text-center">
                              {entry.rule_breaks.length <= 3
                                ? entry.rule_breaks.map((rb, i) => (
                                    <div key={i} className="truncate">{rb.tag_label}</div>
                                  ))
                                : <span className="line-clamp-3">{entry.rule_breaks.map(rb => rb.tag_label).join(', ')}</span>
                              }
                            </div>
                          ) : (
                            <span className="text-[9px] text-white/40 leading-tight mt-1">No Rules Broken!</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Yearly View */}
          {view === 'yearly' && (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Month labels */}
                <div className="flex mb-1" style={{ paddingLeft: '36px', height: '18px' }}>
                  {monthLabels.map((ml, i) => {
                    const nextWeekIndex = i < monthLabels.length - 1
                      ? monthLabels[i + 1].weekIndex
                      : weeks.length;
                    const span = nextWeekIndex - ml.weekIndex;
                    const widthPx = span * 13 - 2;
                    // Skip labels that are too narrow to display
                    if (widthPx < 20) return null;
                    return (
                      <div
                        key={i}
                        className="text-xs text-muted-foreground flex-shrink-0 overflow-hidden"
                        style={{ width: `${widthPx}px` }}
                      >
                        {ml.label}
                      </div>
                    );
                  })}
                </div>

                {/* Heatmap grid */}
                <div className="flex gap-0.5 pb-2">
                  {/* Day labels */}
                  <div className="flex flex-col gap-0.5 mr-1 flex-shrink-0">
                    {dayLabels.map((label, i) => (
                      <div key={i} className="h-[11px] w-8 text-[10px] text-muted-foreground flex items-center justify-end pr-1">
                        {label}
                      </div>
                    ))}
                  </div>

                  {/* Weeks */}
                  <div className="flex gap-0.5">
                    {weeks.map((week, weekIdx) => (
                      <div key={weekIdx} className="flex flex-col gap-0.5">
                        {week.map((entry, dayIdx) => {
                          const hasData = entry !== null;
                          return (
                            <div
                              key={dayIdx}
                              className="w-[11px] h-[11px] rounded-sm cursor-pointer transition-transform hover:scale-150"
                              style={{
                                backgroundColor: getCellColor(entry),
                                opacity: getCellOpacity(entry),
                              }}
                              title={hasData ? getTooltipText(entry) : 'No data'}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SEVERITY_COLORS.high }} /> High Severity
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SEVERITY_COLORS.medium }} /> Medium Severity
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: SEVERITY_COLORS.low }} /> Low Severity
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: NO_DATA_COLOR, opacity: 0.4 }} /> No Rule Breaks
            </span>
            <span className="text-[10px] italic">
              (Opacity indicates frequency: darker = more violations)
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default RuleBreakingCalendar;